import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PARTNER_ROLES } from '../Constants/partnerRoles';
import {
  PARTNER_PORTAL_HOME,
  PARTNER_PORTAL_PROJECT_DETAIL_BASE,
  PARTNER_PORTAL_LEADERSHIP_PIPELINE,
  PARTNER_PORTAL_MY_PROJECTS,
  PARTNER_PORTAL_PARTNER_COMPANIES,
  PARTNER_PORTAL_PAE_OPERATIONS,
  PARTNER_PORTAL_PAE_PROJECT_DETAIL_BASE,
  PARTNER_PORTAL_PAE_PROJECT_BASE,
  PARTNER_PORTAL_PAE_QUEUE,
  PARTNER_PORTAL_SUBMIT,
} from '../Constants/partnerRoutes';
import { partnerRoleService } from '../Services/partnerRoleService';
import { useFeatureFlags } from '../../../../FeatureFlags/FeatureFlagsContext';
import { FEATURE_FLAGS } from '../../../../FeatureFlags/featureFlagConstants';

const PARTNER_ROUTES = [
  PARTNER_PORTAL_SUBMIT,
  PARTNER_PORTAL_MY_PROJECTS,
  PARTNER_PORTAL_PROJECT_DETAIL_BASE,
];

const PAE_ROUTES = [
  PARTNER_PORTAL_PAE_QUEUE,
  PARTNER_PORTAL_PAE_PROJECT_DETAIL_BASE,
  PARTNER_PORTAL_PAE_PROJECT_BASE,
  PARTNER_PORTAL_PARTNER_COMPANIES,
  PARTNER_PORTAL_PAE_OPERATIONS,
];

const LEADERSHIP_ROUTES = [
  PARTNER_PORTAL_LEADERSHIP_PIPELINE,
];

export const usePartnerRoleAccess = () => {
  const currentUser = useSelector((state) => state.auth?.currentUser);
  const { isFeatureEnabled } = useFeatureFlags();
  const isPartnerPortalEnabled = isFeatureEnabled(FEATURE_FLAGS.EnablePartnerPortal);

  return useMemo(() => {
    const role = partnerRoleService.resolveRole(currentUser);
    const isAdmin = role === PARTNER_ROLES.ADMIN;
    const isPAE = role === PARTNER_ROLES.PAE;
    const isLeadership = role === PARTNER_ROLES.LEADERSHIP;

    const hasFullPartnerPortalAccess = isAdmin || isPAE || isLeadership;

    const hasAccess = partnerRoleService.hasPartnerPortalAccess(currentUser);
    const canAccessPartnerPages = hasFullPartnerPortalAccess
      || partnerRoleService.isPartnerUser(currentUser);
    const canSubmitAsPartner = partnerRoleService.canSubmitAsPartner(currentUser)
      && !partnerRoleService.canSubmitOnBehalf(currentUser);
    const canSubmitOnBehalf = partnerRoleService.canSubmitOnBehalf(currentUser);
    const canAccessSubmitProject = partnerRoleService.canAccessSubmitProject(currentUser);
    const canSubmitPartnerProject = canAccessSubmitProject;
    const canPerformPAEOperations = hasFullPartnerPortalAccess
      || partnerRoleService.canPerformPAEOperations(currentUser);
    const canAccessPAEPages = hasFullPartnerPortalAccess || isLeadership;
    const canAccessLeadershipPages = hasFullPartnerPortalAccess || isLeadership;

    const allowedRoutes = [
      PARTNER_PORTAL_HOME,
      ...(canAccessPartnerPages ? PARTNER_ROUTES.filter((path) => (
        path !== PARTNER_PORTAL_SUBMIT || canAccessSubmitProject
      )) : []),
      ...(canAccessSubmitProject && !canAccessPartnerPages ? [PARTNER_PORTAL_SUBMIT] : []),
      ...(canAccessPAEPages ? PAE_ROUTES : []),
      ...(canAccessLeadershipPages ? LEADERSHIP_ROUTES : []),
    ];

    return {
      role,
      hasAccess,
      allowedRoutes,
      canAccessPartnerPages,
      canSubmitPartnerProject,
      canSubmitAsPartner,
      canSubmitOnBehalf,
      canAccessSubmitProject,
      canAccessPAEPages,
      canPerformPAEOperations,
      canAccessLeadershipPages,
      getDefaultRoute() {
        if (canAccessPAEPages) {
          return PARTNER_PORTAL_PAE_QUEUE;
        }
        if (canAccessPartnerPages) {
          return PARTNER_PORTAL_MY_PROJECTS;
        }
        return null;
      },
      canAccessPath(path) {
        if (!hasAccess) {
          return false;
        }

        if (path === PARTNER_PORTAL_HOME || path === `${PARTNER_PORTAL_HOME}/`) {
          return true;
        }

        return allowedRoutes.some((allowedPath) => {
          if (allowedPath === PARTNER_PORTAL_HOME) {
            return false;
          }
          return path === allowedPath || path.startsWith(`${allowedPath}/`);
        });
      },
    };
  }, [currentUser, isPartnerPortalEnabled]);
};
